import { useMutation, useQuery } from 'react-query'
import { axiosClient } from 'utils/axios-util'
import { useDynamicManageStore } from 'store/useDynamicManageStore'
import { useDocPreviewStore } from 'store/useDocPreviewStore'
import { useDocListStore } from 'store/useDocListStore'
import { useNotificationStore } from 'store/useNotificationStore'
import { useErrorStore } from 'store/useErrorStore'

import {
    APPLICANT,
    COBUYER,
    CO_APPLICANT,
    DOCUMENTS,
    FILE_SIZE_STATUS_CODE,
    PDF_MIME_TYPE,
    STATUS_LIST,
    SUCCESS_STATUS_CODE,
    FILE_SIZE_ERROR,
    HARMFUL_JAVASCRIPT_ERROR_CODE,
    HARMFUL_VIRUS_ERROR_CODE,
    HARMFUL_JAVASCRIPT_ERROR,
    HARMFUL_VIRUS_ERROR,
    HARMFUL_STATUS_CODE,
    DOC_MAX_SIZE
} from 'constants/docUploadConstant'
import { docUploadAnalytics } from 'analytics/docUploadAnalytics'

const { INPROGRESS, UPLOADED, ERROR } = STATUS_LIST

const getDocList = ({ dealJacketId, dealerId, country }) => {
    return axiosClient.get(`/${DOCUMENTS}/${dealJacketId}/${dealerId}?country=${country}`)
}

export const getDoc = (docInfo) => {
    const { dealJacketId, dealerId, country } = useDynamicManageStore.getState()
    const { setLoadingProgress } = useDocPreviewStore.getState()

    return axiosClient.get(`/${DOCUMENTS}/${dealJacketId}/${dealerId}/${docInfo.documentId}?country=${country}`, {
        onDownloadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setLoadingProgress?.(progress)
        }
    })
}

export const deleteDoc = (documentId) => {
    const { dealJacketId, dealerId, country } = useDynamicManageStore.getState()

    return axiosClient.delete(`/${DOCUMENTS}/${dealJacketId}/${dealerId}/${documentId}?country=${country}`)
}

export const addDoc = ({ preview, backSidePreview }) => {
    const { dealJacketId, dealerId, country } = useDynamicManageStore.getState()
    const { updateDoc } = useDocListStore.getState()

    const documentInfo = []
    if (preview?.base64Data) {
        documentInfo.push({ data: preview.base64Data, mimeType: preview.mimeType })
    }
    if (backSidePreview?.base64Data) {
        documentInfo.push({ data: backSidePreview.base64Data, mimeType: backSidePreview.mimeType })
    }

    const payload = {
        dealJacketId: dealJacketId,
        dealerId: dealerId,
        documentType: preview.documentType,
        documentName: preview.documentName,
        customerRole: preview.actor,
        documentInfo: documentInfo
    }

    let progressTotal = 0

    return axiosClient.post(`/${DOCUMENTS}?country=${country}`, payload, {
        onUploadProgress: (progressEvent) => {
            progressTotal = progressEvent.total
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 50)
            updateDoc(preview.id, { progressCount: progress, status: INPROGRESS })
        },
        onDownloadProgress: (progressEvent) => {
            const progress = 50 + Math.round((progressEvent.loaded / progressTotal) * 50)
            updateDoc(preview.id, { progressCount: progress, status: INPROGRESS })
        },
        maxContentLength: DOC_MAX_SIZE
    })
}

const backToDM = (backToDM) => {
    const { showError } = useErrorStore.getState()
    showError({ backToDM })
}

export const useDocListData = () => {
    const { dealJacketId, dealerId, country } = useDynamicManageStore((state) => state)
    const { setDocs, setDealerName } = useDocListStore((state) => state)

    return useQuery({
        queryKey: ['doc-list', dealJacketId, dealerId],
        queryFn: () => getDocList({ dealJacketId, dealerId, country }),
        onSuccess: (response) => {
            if (response) {
                response.data.documentList = (response?.data?.documentList || [])
                    .map((item, index) => {
                        item.id = index
                        item.actorRole = (item?.actor || '').toLowerCase() === COBUYER ? CO_APPLICANT : APPLICANT
                        return item
                    })
                    .sort((a, b) => {
                        const textA = (a?.documentName || '').toUpperCase() + a.actorRole
                        const textB = (b?.documentName || '').toUpperCase() + b.actorRole
                        let result
                        if (textA < textB) {
                            result = -1
                        } else if (textA > textB) {
                            result = 1
                        } else {
                            result = 0
                        }
                        return result
                    })
                setDocs(response.data.documentList)
                setDealerName(response?.data?.miscellaneousData?.dealerName)
            }
        },
        onError: () => {
            backToDM(true)
        }
    })
}

export const useDeleteDocData = () => {
    return useMutation(deleteDoc, {
        onSuccess: ({ status }, documentId) => {
            if (status === SUCCESS_STATUS_CODE && documentId) {
                const { deleteDoc } = useDocListStore.getState()
                deleteDoc(documentId)
            }
        },
        onError: () => {
            const { resetDocPreviewStore } = useDocPreviewStore.getState()
            resetDocPreviewStore()
            backToDM(false)
        }
    })
}

export const useAddDocData = () => {
    return useMutation(addDoc, {
        onSuccess: ({ data, status: statusCode } = {}, { preview }) => {
            const { error: { messages } = { messages: [] } } = data || {}
            const { updateDoc } = useDocListStore.getState()
            const { showFileSizeNotification, showHarmfulNotification } = useNotificationStore.getState()

            if (statusCode === FILE_SIZE_STATUS_CODE) {
                docUploadAnalytics.track('error', FILE_SIZE_ERROR)
                showFileSizeNotification()
            }
            if (statusCode === HARMFUL_STATUS_CODE) {
                if (messages.includes(HARMFUL_JAVASCRIPT_ERROR_CODE)) {
                    docUploadAnalytics.track('error', HARMFUL_JAVASCRIPT_ERROR)
                }

                if (messages.includes(HARMFUL_VIRUS_ERROR_CODE)) {
                    docUploadAnalytics.track('error', HARMFUL_VIRUS_ERROR)
                }

                showHarmfulNotification()
            }
            if (statusCode === SUCCESS_STATUS_CODE) {
                updateDoc(preview.id, {
                    progressCount: 100,
                    status: UPLOADED,
                    documentId: data.documentId,
                    mimeType: data.mimeType,
                    base64Data: data.data
                })
            } else {
                updateDoc(preview.id, {
                    status: ERROR,
                    mimeType: '',
                    base64Data: ''
                })
            }
        }
    })
}

export const useGetDocData = () => {
    return useMutation(getDoc, {
        onSuccess: (response, docInfo) => {
            const data = response?.data
            const { updateDoc } = useDocListStore.getState()
            const { setLoadingProgress, setIsLoading } = useDocPreviewStore.getState()

            updateDoc(docInfo.id, {
                base64Data: data.data,
                mimeType: PDF_MIME_TYPE
            })

            setTimeout(() => {
                setIsLoading(false)
                setLoadingProgress(0)
            }, 500)
        },
        onError: () => {
            const { resetDocPreviewStore } = useDocPreviewStore.getState()
            resetDocPreviewStore()
            backToDM(false)
        }
    })
}

export const getDocName = ({ documentName = '', actorRole = '' }) => {
    return `${documentName} (${actorRole})`
}

export const getDocNameWithActor = (documentName = '', actorRole = '') => {
    return `${documentName} ${actorRole}`
}
